import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { BUS_AIRCRAFT_CODE } from '@fcom/booking/modules/ticket-selection/interfaces';
import { BoundType, Duration, LocationInfoMap } from '@fcom/dapi';
import {
  FinnairBoundItem,
  FinnairDisruptedBoundItem,
  FinnairItineraryItem,
  FinnairItineraryItemFlight,
  FinnairItineraryItemFlightStatus,
  FinnairItineraryItemLayover,
} from '@fcom/dapi/api/models';
import { NotificationLayout, NotificationTheme } from '@fcom/ui-components';
import { LocalizationPipe } from '@fcom/ui-translate';

import { EndPointChanges, FlightChanges } from '../../interfaces/index';
import { isFlight } from '../../utils/flight.utils';

@Component({
  selector: 'fin-itinerary-timeline',
  styleUrls: ['./itinerary.scss', 'arrow-block.scss'],
  templateUrl: './itinerary-timeline.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ItineraryTimelineComponent implements OnChanges {
  readonly BUS_AIRCRAFT_CODE = BUS_AIRCRAFT_CODE;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  private finLocalization = inject(LocalizationPipe);

  /**
   * The translation prefix for strings that are not common to both departure and return flights.
   * E.g. "Departure" vs. "Return"
   */
  @Input()
  nonCommonTranslationPrefix: string;

  @Input()
  boundType: BoundType = null;

  @Input()
  bound: FinnairBoundItem | FinnairDisruptedBoundItem;

  @Input()
  locations: LocationInfoMap;

  @Input()
  isBookingFlow = false;

  @Input()
  title: string;

  @Input()
  legChanges: Map<string, FlightChanges> = new Map();

  @Input()
  departureChanges: EndPointChanges;

  @Input()
  arrivalChanges: EndPointChanges;

  @Input()
  boundChangeDuration: Duration;

  @Input()
  showNoChangesNotification: boolean;

  fareDetailMap = new Map<string, string>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['bound']) {
      this.computeFareDetails();
    }
  }

  toType(t: FinnairItineraryItem): FinnairItineraryItemFlight | FinnairItineraryItemLayover {
    return 'technicalStops' in t ? (t as FinnairItineraryItemFlight) : (t as FinnairItineraryItemLayover);
  }

  computeFareDetails(): void {
    this.fareDetailMap =
      this.bound?.itinerary?.filter(isFlight).reduce((flightDetailDictionary, flight) => {
        flightDetailDictionary.set(flight.id, this.getFareDetail(flight));
        return flightDetailDictionary;
      }, new Map<string, string>()) ?? new Map();
  }

  getFareDetail = (flight: FinnairItineraryItemFlight): string | undefined => {
    const isUpgraded = flight.upgradeItem?.status === FinnairItineraryItemFlightStatus.CONFIRMED;
    const bookingClass = flight.bookingClass ?? '';

    if (isUpgraded || !this.bound?.fareFamily?.name) {
      const cabinClass = flight.cabinClass?.toLowerCase();
      const travelClassDetail = cabinClass ? this.finLocalization.transform(`travelClass.${cabinClass}`) : '';
      return `${travelClassDetail}${bookingClass ? ' ' + bookingClass : ''}`;
    }
    const suffix = bookingClass ? ` (${bookingClass})` : '';
    return `${this.bound.fareFamily.name}${suffix}`;
  };
}
